import { Navigate, generatePath, useParams } from 'react-router-dom';

import { usePartnersReadEvent } from '@/api/hooks';
import { type Partner } from '@/api/routes/partners/partners.api';
import LoadErrorState from '@/components/LoadErrorState';
import EventEditor from '@/domain/events/components/EventEditor';
import EventFormSkeleton from '@/domain/events/components/EventFormSkeleton';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';

interface Props {
  partner: Partner;
}

/** `/events/new` and `/events/:eventId/edit` (P09). A canceled event cannot be edited. */
const EventForm = ({ partner }: Props) => {
  const t = useTranslate('domain.events.EventForm');
  const { eventId } = useParams<{ eventId: string }>();
  const { data: event, isLoading, isError, refetch } = usePartnersReadEvent(eventId);

  if (!eventId) return <EventEditor partner={partner} />;
  if (isLoading) return <EventFormSkeleton />;
  if (isError || !event) {
    return <LoadErrorState message={t('loadError')} onRetry={() => refetch()} />;
  }
  if (event.canceledAt) {
    return <Navigate to={generatePath(ScreenRoutes.EVENT_DETAIL, { eventId })} replace />;
  }

  // Keyed by event: a refetch after saving must not keep the previous form state.
  return <EventEditor key={event.updatedAt} partner={partner} event={event} />;
};

export default EventForm;
